"use client";

import { useState } from "react";
import { useMarketingText } from "@/lib/i18n/marketingClient";
import { useI18n } from "@/components/providers/I18nProvider";
import { uiIntlLocale } from "@/lib/i18n/languages";

export interface Beat {
  /** Beat name. */
  name: string;
  /** Where it lands, as a fraction of the finished book. */
  at: number;
  /** Our own one-line gloss on what the beat is doing. */
  does: string;
}

/**
 * Turns a target word count into the word each beat should land on.
 *
 * "beat sheet calculator" and "beat sheet generator" both show up as
 * tool-shaped queries, and the former has already earned this site an
 * impression. The query wants arithmetic done, so the page does the
 * arithmetic — a table of percentages makes the reader do it themselves,
 * which is the thing they searched to avoid.
 */
export function BeatSheetCalculator({ beats }: { beats: Beat[] }) {
  const { locale, t } = useI18n();
  const [target, setTarget] = useState(90_000);
  const numberLocale = uiIntlLocale(locale);
  const formatNumber = (value: number) => value.toLocaleString(numberLocale);
  const localize = useMarketingText();

  const wordAt = (fraction: number) => Math.round((target * fraction) / 500) * 500;

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-muted/30 px-4 py-3">
        <label htmlFor="beat-target" className="text-sm font-medium">
          {t("marketing.beat.targetWordCount")}
        </label>
        <input
          id="beat-target"
          type="number"
          min={10_000}
          max={400_000}
          step={5_000}
          value={target}
          onChange={(e) => setTarget(Math.max(0, Number(e.target.value) || 0))}
          className="min-h-[40px] w-36 rounded-lg border border-border bg-background px-3 text-sm tabular-nums"
        />
        <span className="text-xs text-muted-foreground">
          {t("marketing.beat.roundingNote")}
        </span>
      </div>

      <div className="mt-4 overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-2.5 text-left font-medium">{t("marketing.beat.beat")}</th>
              <th className="px-4 py-2.5 text-left font-medium">{t("marketing.beat.percent")}</th>
              <th className="px-4 py-2.5 text-left font-medium">{t("marketing.beat.word")}</th>
              <th className="px-4 py-2.5 text-left font-medium">{t("marketing.beat.whatItDoes")}</th>
            </tr>
          </thead>
          <tbody>
            {beats.map((beat) => (
              <tr key={beat.name} className="border-t border-border">
                <td className="px-4 py-2.5 font-medium">{localize(beat.name)}</td>
                <td className="px-4 py-2.5 tabular-nums text-muted-foreground">
                  {formatNumber(Math.round(beat.at * 100))}{t("marketing.beat.percent")}
                </td>
                <td className="px-4 py-2.5 tabular-nums text-muted-foreground">
                  {formatNumber(wordAt(beat.at))}
                </td>
                <td className="px-4 py-2.5 text-muted-foreground">{localize(beat.does)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
